import { CaseSummaryTooltip } from '@/citator/components/CaseSummaryTooltip'
import { openStandaloneViewerTab } from '@/standalone-reference-viewer/utils/open-standalone-viewer-tab'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { Link } from 'react-router-dom'
import { OpenNew } from '@/assets/icons'
import { CaseReviewInfoTag } from '@/citator/components/CaseReviewInfoTag'
import { isDisplayableCaseReview, type DisplayableCaseReview } from '@/citator/schema/case-review-schema'
import type { CitationMetadata } from '@/citator/schema/citation-metadata-schema'
import type { CitingCase } from '@/citator/schema/case-review-schema'

/**
 * Shared list-item for all three citation-analysis accordions. Citing cases pass a
 * `CitingCase` (review badge + explanation rendered when displayable); the other sections pass
 * `CitationMetadata` and the review markup is skipped.
 */
export type CitatorCaseListItemProps = {
  case: CitingCase | CitationMetadata
  /** 1-based position number; caller owns numbering so filtered lists stay contiguous. */
  number: number
  /** Standalone reference viewer URL. Caller builds it so analytics params can be attached. */
  href?: string
  onLinkClick?: () => void
  /** Analytics-only: fires when the user opens the treatment-outcome popover. */
  onReviewClick?: (review: DisplayableCaseReview) => void
}

export function CitatorCaseListItem({ case: c, number, href, onLinkClick, onReviewClick }: CitatorCaseListItemProps) {
  const review = 'review_label' in c ? c.review_label : null
  const explanation = 'explanation' in c ? c.explanation : null
  const formattedCitation = c.bb_citation ?? c.citation ?? null
  const subtitle = formattedCitation === c.name ? null : formattedCitation
  const hasDisplayableReview = isDisplayableCaseReview(review)

  return (
    <div className="flex gap-1 items-start w-full">
      <p className="w-10 shrink-0 text-right font-bold text-sm leading-5 text-brand-neutral-950 tracking-[-0.154px]">{number}.</p>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex flex-col gap-1">
          {href ? (
            <CaseSummaryTooltip
              title={c.bb_citation ?? c.citation ?? c.name}
              shortSummary={c.short_summary}
              caseStatus={c.case_status}
              onViewReference={() => {
                onLinkClick?.()
                openStandaloneViewerTab(href)
              }}
              onViewCitationAnalysis={() => {
                const analysisUrl = new URL(href, window.location.origin)
                analysisUrl.searchParams.set('tab', CaseLawReferenceTab.citator)
                analysisUrl.searchParams.delete('section')
                openStandaloneViewerTab(analysisUrl.pathname + analysisUrl.search)
              }}
            >
              <Link
                to={href}
                target="_blank"
                onClick={onLinkClick}
                className="inline-flex items-center gap-1 self-start text-sm font-medium leading-5 text-brand-700 underline hover:text-brand-800"
              >
                {c.name}
                <OpenNew className="w-4 h-4" />
              </Link>
            </CaseSummaryTooltip>
          ) : (
            <p className="text-sm font-medium leading-5 text-brand-neutral-950">{c.name}</p>
          )}
          {subtitle && <p className="text-sm font-normal leading-5 text-brand-neutral-950">{subtitle}</p>}
        </div>
        {hasDisplayableReview && (
          <div className="flex flex-col gap-2">
            <CaseReviewInfoTag review={review} onTagClick={onReviewClick ? () => onReviewClick(review) : undefined} />
            {explanation && <p className="text-sm font-normal leading-5 text-brand-neutral-950 whitespace-pre-wrap">{explanation}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
